import {
	each,
	groupBy,
	isArray,
	isEmpty,
	isPlainObject,
	isString,
	template,
} from "./barrels/lodash";

/**
 * Verifies if a value contains only digits (allows empty string)
 *
 * @param {string} value - The value to verify
 * @returns {boolean} True if value contains only digits or is empty, false otherwise
 */
export const verifyOnlyDigits = (value) => {
	const onlyDigitsRegex = /^\d*$/;
	return onlyDigitsRegex.test(value);
};

/**
 * Verifies if a value is a valid GSTIN (GST Identification Number)
 *
 * @param {string} value - The GSTIN value to verify (will be converted to uppercase)
 * @returns {boolean} True if value is a valid GSTIN format, false otherwise
 */
export const verifyGSTIN = (value) => {
	if (!value) {
		return false;
	}
	const gstinRegex =
		/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
	return gstinRegex.test(value.toUpperCase());
};

/**
 * Verifies if a value contains only alphabetic characters and spaces
 *
 * @param {string} value - The value to verify
 * @returns {boolean} True if value contains only alphabetic characters and spaces, false otherwise
 */
export const verifyOnlyAlphabeticWithSpaces = (value) => {
	if (!value) {
		return false;
	}
	const onlyAlphabeticWithSpacesRegex = /[^a-zA-Z\s]/g;
	return !onlyAlphabeticWithSpacesRegex.test(value);
};

/**
 * Filters a string to keep only alphanumeric characters and spaces
 * Removes all characters that are not letters, numbers, or spaces
 *
 * @param {string} value - The string to filter
 * @returns {string} The filtered string containing only alphanumeric characters and spaces
 */
export const filterAlphaNumericWithSpaces = (value) => {
	if (!value) {
		return "";
	}
	const alphaNumericSpaceFilter = /[^a-zA-Z0-9 ]/g;
	return value.replace(alphaNumericSpaceFilter, "");
};

/**
 * Verifies if a value is a valid decimal number (allows digits, single decimal point)
 *
 * @param {string} value - The value to verify
 * @returns {boolean} True if value is a valid decimal number format, false otherwise
 */
export const verifyDecimalNumberOnly = (value) => {
	if (!value) {
		return false;
	}
	const decimalNumberOnlyRegex = /^\d*\.?\d*$/;
	return decimalNumberOnlyRegex.test(value);
};

/**
 * Filters a string to keep only numeric digits
 * Removes all characters that are not digits (0-9)
 *
 * @param {string} value - The string to filter
 * @returns {string} The filtered string containing only digits
 */
export const filterOnlyNumber = (value) => {
	if (!value) {
		return "";
	}
	const onlyNumberRegex = /[^0-9]/g;
	return value.replace(onlyNumberRegex, "");
};

/**
 * Verifies if a value is a valid 10-digit contact number
 *
 * @param {string} value - The contact number to verify
 * @returns {boolean} True if value is exactly 10 digits, false otherwise
 */
export const verifyContactNumber = (value) => {
	if (!value) {
		return false;
	}
	const contactNumberRegex = /^\d{10}$/;
	return contactNumberRegex.test(value.trim());
};

/**
 * Filters a string to keep only decimal number characters (digits and decimal point)
 * Removes all characters that are not digits or decimal points
 *
 * @param {string} value - The string to filter
 * @returns {string} The filtered string containing only digits and decimal points
 */
export const filterDecimalNumber = (value) => {
	if (!value) {
		return "";
	}
	const decimalNumberFilterRegex = /[^0-9.]/g;
	return value.replace(decimalNumberFilterRegex, "");
};

export const verifyEmail = (value) => {
	const emailRex =
		/^(?:(?:[^<>()[\]\\.,;:\s@"]+(?:\.[^<>()[\]\\.,;:\s@"]+)*)|(?:".+"))@(?:(?:\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(?:(?:[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	if (emailRex.test(value)) {
		return true;
	}
	return false;
};

export const verifyLength = (value, length) => {
	if (value.length >= length) {
		return true;
	}
	return false;
};

export const verifyExactLength = (value, length) => {
	if (value.length === length) {
		return true;
	}
	return false;
};

export const verifyMaxLength = (value, length) => {
	if (value.length <= length) {
		return true;
	}
	return false;
};

export const verifyMinLength = (value, length) => {
	if (value.length >= length) {
		return true;
	}
	return false;
};

export const verifyNumericLength = (value, length) => {
	const regex = /^[0-9]*$/;
	return regex.test(value) && value.length <= length;
};

export const verifyNumber = (value) => {
	const numberRex = new RegExp("^[0-9]+$");
	if (numberRex.test(value) && value > 0) {
		return true;
	}
	return false;
};

export const verifyInvoiceNumber = (value) => {
	if (!value || value.length === 0) {
		return false;
	}
	if (value.length > 25) {
		return false;
	}
	const numberRex = new RegExp("^[0-9a-zA-Z-/]+$");
	if (numberRex.test(value)) {
		return true;
	}
	return false;
};

export const verifyDecimalNumber = (value) => {
	const numberRex =
		/^(?:[0-9]*[1-9][0-9]*(?:\.[0-9]+)?|[0]*\.[0-9]*[1-9][0-9]*)$/;
	if (numberRex.test(value)) {
		return parseFloat(value) >= 0.01;
	}
	return false;
};

export const validateAlphaNumericWithComma = (value) => {
	const validateAlphaNumericWithCommaRegex = /^[a-zA-Z0-9 ,]+$/;
	return validateAlphaNumericWithCommaRegex.test(value);
};

export const parseErrors = (data, label = "Item") => {
	const _camelCaseToVerbose = (text) => text.replace(/(?=[A-Z])/g, " ");

	const _underscoredToVerbose = (text) => text.replace(/[\d_]/g, " ");

	const _capitalize = (text) => {
		let result = text.toLowerCase();
		result = result.charAt(0).toUpperCase() + result.slice(1);
		return result;
	};

	function _parseErrorItem(item, listPos) {
		const listItemTemplate = template(
			`<li class="list-item">
					<span class="list-item-pos">
						${label} <%= i %>
					</span>
				<%= content %></li>`
		);
		const containerTemplate = template(`<ul class="item"><%= content %></ul>`);
		let output = [];

		each(item, (value, key) => {
			const fieldTemplate = template(
				`<li class="field">
									<span class="name">
										<%- name %>
									</span>
									<%= content %>
								</li>`
			);
			const plainValueTemplate = template(
				`<span class="value"><%- value %></span>`
			);
			let plainValue, listValue, content;

			if (isString(value)) {
				plainValue = value;
			} else if (isArray(value)) {
				if (isString(value[0])) {
					plainValue = value.join(" ");
				} else {
					listValue = _parseErrorList(value);
				}
			} else if (isPlainObject(value)) {
				plainValue = _parseErrorItem(value);
			}

			if (plainValue) {
				content = plainValueTemplate({ value: plainValue });
			} else if (listValue) {
				content = listValue;
			}

			if (content) {
				let processedKey = key;
				if (key === "non_field_errors") {
					processedKey = "";
				} else {
					if (key.search(/[A-Z]/) !== -1) {
						processedKey = _camelCaseToVerbose(key);
					}

					if (processedKey.search(/[\d_]/) !== -1) {
						processedKey = _underscoredToVerbose(processedKey);
					}

					processedKey = _capitalize(processedKey);
				}
				output.push(
					fieldTemplate({
						name: processedKey ? `${processedKey} :` : "",
						content,
					})
				);
			}
		});

		output = output.join("");

		if (output) {
			output = containerTemplate({ content: output });
			if (listPos) {
				output = listItemTemplate({
					i: listPos,
					content: output,
				});
			}
		}
		output = output.replace(/&quot;/g, `"`);
		output = output.replace(/&gt;/g, ">");
		output = output.replace(/&lt;/g, "<");
		return output;
	}

	function _parseErrorList(items) {
		const containerTemplate = template(`<ul class="list"><%= content %></ul>`);
		let output = [];

		each(items, (item, i) => {
			if (!isEmpty(item)) {
				output.push(_parseErrorItem(item, i + 1));
			}
		});

		output = output.join("");

		if (output) {
			output = containerTemplate({ content: output });
		}

		return output;
	}

	if (isArray(data)) {
		return _parseErrorList(data);
	} else if (isPlainObject(data)) {
		return _parseErrorItem(data);
	} else {
		return _parseErrorItem(data);
	}
};

export const parseUploadErrors = (uploadDataErrors) => {
	let errors = [];
	for (const rowNum of Object.keys(uploadDataErrors)) {
		for (const rowName of Object.keys(uploadDataErrors[rowNum])) {
			errors = [
				...errors,
				{
					row: parseInt(rowNum, 10),
					field: rowName,
					err: uploadDataErrors[rowNum][rowName][0],
				},
			];
		}
	}
	errors = groupBy(errors, "row");
	return errors;
};

export const isValidGST = (gst) =>
	/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(gst);

export const isValidPrice = (price) =>
	/^\d+(?:\.\d{0,2})?$/.test(price.toString()) && parseFloat(price) > 0;

export const isValidQuantity = (quantity) =>
	/^[1-9]\d*$/.test(quantity.toString());
